import type { FC, ReactNode } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { ANIMATION_SPEEDS, spacing, transitions } from "../design-tokens";
import { Heading5 } from "./typography";

type AnimatedSectionProps = {
  isVisible?: boolean;
  heading?: string;
  children: ReactNode;
  gap?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl";
  animationType?: "fadeSlide" | "fadeOnly" | "none";
  className?: string;
};

const Container = styled.div<{
  isVisible: boolean;
  animationType: "fadeSlide" | "fadeOnly" | "none";
}>`
  ${(props) => {
    if (props.animationType === "none") {
      return css`
        display: ${props.isVisible ? "block" : "none"};
        box-sizing: border-box;
      `;
    }

    if (props.animationType === "fadeOnly") {
      return css`
        ${transitions.fadeOnly()}
        opacity: ${props.isVisible ? 1 : 0};
        pointer-events: ${props.isVisible ? "auto" : "none"};
        max-height: 800px;
        box-sizing: border-box;
      `;
    }

    // fadeSlide animation
    return css`
      ${transitions.fadeSlide()}
      max-height: ${props.isVisible ? "1500px" : "0"};
      opacity: ${props.isVisible ? 1 : 0};
      transform: translateY(${props.isVisible ? 0 : 8}px);
      overflow: hidden;
      box-sizing: border-box;
      transition: opacity ${ANIMATION_SPEEDS.STANDARD}ms ease-in-out, transform ${ANIMATION_SPEEDS.STANDARD}ms ease-in-out, max-height ${ANIMATION_SPEEDS.STANDARD}ms ease-in-out;

      ${!props.isVisible && css`
        padding: 0;
        margin: 0;
      `}
    `;
  }}
`;

const ContentWrapper = styled.div<{ gap?: string }>`
  display: flex;
  flex-direction: column;
  gap: ${props => props.gap ? spacing[props.gap as keyof typeof spacing] : 0}px;
`;

export const AnimatedSection: FC<AnimatedSectionProps> = ({
  isVisible = true,
  heading,
  children,
  gap,
  animationType = "fadeSlide",
  className,
}) => {
  return (
    <Container
      isVisible={isVisible && !!children}
      animationType={animationType}
      className={className}
    >
      <ContentWrapper gap={gap}>
        {heading && isVisible && <Heading5>{heading}</Heading5>}
        {children}
      </ContentWrapper>
    </Container>
  );
};
